/**
 * Moving an item, and what that costs you.
 *
 * Separate from the panel so the rules can be tested without a browser,
 * and separate from `feedbackTypes.js` so the vocabulary stays a
 * vocabulary. The rules it enforces:
 *
 *   - Only a transition the state machine allows.
 *   - Closing as "won't do" or "duplicate" requires a reply, because the
 *     submitter reads that line and "no" with no reason is how a feedback
 *     channel dies.
 *   - A duplicate has to name what it duplicates.
 *
 * The payload carries only what changed. Sending the whole row back would
 * make two people triaging at once silently overwrite each other.
 *
 * Pure.
 */

import {
  canTransition,
  requiresDuplicateOf,
  requiresReply,
  stateMeta,
} from './feedbackTypes';

export const MAX_REPLY = 2000;
export const MAX_OWNER = 80;

const isBlank = (value) => value === null || value === undefined || String(value).trim() === '';
const text = (value) => (isBlank(value) ? '' : String(value).trim());

/** The form, seeded from the item as it stands. */
export function emptyTriage(item) {
  const row = item || {};
  return {
    state: text(row.state),
    impact: text(row.impact),
    owner: text(row.owner_name) || text(row.owner),
    reply: text(row.reply),
    duplicate_of: text(row.duplicate_of),
  };
}

export function updateTriage(triage, field, value) {
  return { ...(triage || {}), [field]: value };
}

export function validateTriage(triage, item) {
  const row = triage || {};
  const current = text((item || {}).state);
  const target = text(row.state);
  const errors = {};

  if (!target) {
    errors.state = 'Pick a status.';
  } else if (target !== current && !canTransition(current, target)) {
    errors.state = `Cannot move from ${stateMeta(current).label} to ${stateMeta(target).label}.`;
  }

  if (requiresReply(target) && !text(row.reply)) {
    errors.reply = 'Say why. The person who raised it reads this.';
  }

  if (text(row.reply).length > MAX_REPLY) {
    errors.reply = `Keep the reply under ${MAX_REPLY} characters.`;
  }

  if (text(row.owner).length > MAX_OWNER) {
    errors.owner = `Keep the owner under ${MAX_OWNER} characters.`;
  }

  if (requiresDuplicateOf(target) && !text(row.duplicate_of)) {
    errors.duplicate_of = 'Which item does this duplicate?';
  }

  return { ok: Object.keys(errors).length === 0, errors };
}

const FIELDS = ['state', 'impact', 'owner', 'reply', 'duplicate_of'];

/** Only what moved, so concurrent triage does not clobber. */
export function triagePayload(triage, item) {
  const row = triage || {};
  const before = emptyTriage(item);
  return FIELDS.reduce((acc, field) => {
    const next = text(row[field]);
    return next === before[field] ? acc : { ...acc, [field]: next };
  }, {});
}

/** Nothing to send means nothing to save -- the button stays quiet. */
export function hasChanges(triage, item) {
  return Object.keys(triagePayload(triage, item)).length > 0;
}
